import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiErrorCode } from "@/api/client";
import { confirmPasswordReset, requestPasswordReset } from "@/api/endpoints";
import { Button, Card, Input } from "@/components/ui/primitives";

/**
 * Both halves of "I forgot my password", on one page.
 *
 * Without a token in the URL it asks for an address; with one it asks for the
 * new password. The e-mail links straight here with `?token=…`, so the common
 * path skips the first half entirely — but somebody who typed the address by
 * hand, or whose mail client stripped the link, can still paste the code.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(params.get("token") ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function askForLink(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      // Shown whatever the answer was: the server does not say whether the
      // address is registered, and neither should this screen.
      setSent(true);
    } catch {
      setError("Sunucuya ulaşılamadı");
    } finally {
      setBusy(false);
    }
  }

  async function setNewPassword(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("Şifreler birbirini tutmuyor");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmPasswordReset(token.trim(), password);
      setDone(true);
    } catch (caught) {
      const code = apiErrorCode(caught);
      setError(
        code === "RESET_INVALID"
          ? "Bu bağlantının süresi dolmuş ya da daha önce kullanılmış. Yeni bir tane iste."
          : code === "VALIDATION_ERROR"
            ? "Şifre en az 8 karakter olmalı"
            : code
              ? "Şifre değiştirilemedi"
              : "Sunucuya ulaşılamadı",
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Shell title="Şifren değişti">
        <p className="text-sm text-muted-foreground">
          Yeni şifrenle giriş yapabilirsin. Güvenlik için diğer bütün
          cihazlardaki oturumların kapatıldı.
        </p>
        <Button className="w-full" onClick={() => navigate("/login")}>
          Girişe dön
        </Button>
      </Shell>
    );
  }

  if (token) {
    return (
      <Shell title="Yeni şifre belirle">
        <form className="space-y-4" onSubmit={setNewPassword}>
          {!params.get("token") && (
            <Field label="Sıfırlama kodu">
              <Input value={token} onChange={(e) => setToken(e.target.value)} required />
            </Field>
          )}
          <Field label="Yeni şifre">
            <Input
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Yeni şifre (tekrar)">
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </Field>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy}>
            Şifreyi değiştir
          </Button>
        </form>
      </Shell>
    );
  }

  if (sent) {
    return (
      <Shell title="Postanı kontrol et">
        <p className="text-sm text-muted-foreground">
          Bu adres kayıtlıysa şifre sıfırlama bağlantısı gönderildi. Bağlantı
          kısa süre geçerli ve yalnızca bir kez kullanılabilir.
        </p>
        <Button variant="ghost" className="w-full" onClick={() => setSent(false)}>
          Başka bir adres dene
        </Button>
      </Shell>
    );
  }

  return (
    <Shell title="Şifreni sıfırla">
      <form className="space-y-4" onSubmit={askForLink}>
        <Field label="E-posta">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy}>
          Sıfırlama bağlantısı gönder
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => navigate("/login")}>
          Girişe dön
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm space-y-5 p-6">
        <h1 className="text-lg font-bold tracking-tight">{title}</h1>
        {children}
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
